import { call, delay } from 'redux-saga/effects';
import type { CallEffect } from 'redux-saga/effects';
import { AxiosError } from 'axios';

export interface RetryableError {
	isAxiosError?: boolean;
	response?: {
		status?: number;
	};
	code?: string;
	message?: string;
}

export type RetryableAxiosError = AxiosError & RetryableError;

export interface RetryConfig<TError = RetryableError> {
	maxAttempts?: number;
	delayMs?: number;
	shouldRetry?: (error: TError) => boolean;
}

const RETRYABLE_NETWORK_ERROR_CODES = new Set([
	'ECONNRESET', // Connection reset by peer - often transient
	'ETIMEDOUT', // Request timeout - network congestion or service issue
	'ENOTFOUND', // DNS lookup failed - may resolve with retry
	'ECONNREFUSED', // Connection refused - service may be temporarily down
	'EHOSTUNREACH', // Host unreachable - routing issue may resolve
	'ENETUNREACH', // Network unreachable - network issue may resolve
	'ECONNABORTED', // Connection aborted - often transient
	'EPIPE', // Broken pipe - connection issue
	'EPROTO', // Protocol error - may be transient
]);

/**
 * Determines if an error should be retried based on HTTP retry logic
 *
 * This function implements retry logic for HTTP requests:
 * - Retries network errors (no response received)
 * - Retries 408 Request Timeout
 * - Retries 429 Too Many Requests
 * - Retries 500+ server errors
 * - Retries specific network error codes (ECONNRESET, ETIMEDOUT, etc.)
 *
 * @param {TError} error - The error to evaluate (can be Axios or non-Axios error)
 * @returns {boolean} True if the error should be retried, false otherwise
 */
export const shouldRetryOnError = <TError extends RetryableError>(
	error: TError,
): boolean => {
	// Handle Axios errors
	if (error?.isAxiosError) {
		const status = error.response?.status;
		if (
			!status || // No response - should retry (network error)
			status === 408 ||
			status === 429 || // Request Timeout, Too Many Requests
			(status >= 500 && status < 600) // Server errors (5xx) - should retry
		) {
			return true;
		}
	}

	// Handle non-Axios errors - only retry specific network error codes
	return RETRYABLE_NETWORK_ERROR_CODES.has(error?.code ?? '');
};

const defaultConfig: Required<RetryConfig<RetryableError>> = {
	maxAttempts: 5,
	delayMs: 1500,
	shouldRetry: shouldRetryOnError,
};

/**
 * A saga utility that retries a function call with configurable retry logic
 * @param {TFunc} fn - The function to be called and potentially retried
 * @param {Parameters<TFunc>} [args=[]] - Arguments to pass to the function (strongly typed to match fn's parameters)
 * @param {RetryConfig<TError>} [config={}] - Configuration for the retry mechanism
 * @returns {T} The result of the successful function call
 * @throws {TError} The last error encountered if all retries fail
 */
export function* retryAxiosCall<
	T,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	TFunc extends (...args: any[]) => Promise<T>,
	TError extends RetryableError = RetryableError,
>(
	fn: TFunc,
	args: Parameters<TFunc> = [] as unknown as Parameters<TFunc>,
	config: RetryConfig<TError> = {},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<CallEffect<T>, T, any> {
	const finalConfig = { ...defaultConfig, ...config } as Required<
		RetryConfig<TError>
	>;
	const { maxAttempts, delayMs, shouldRetry } = finalConfig;

	let lastError: TError | undefined;

	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			return yield call(fn, ...args);
		} catch (error) {
			lastError = error as TError;

			if (attempt === maxAttempts || !shouldRetry(error as TError)) {
				throw error;
			}

			yield delay(delayMs);
		}
	}

	throw lastError;
}
